import React, { useMemo, useState } from "react";
import { useStore } from "../store";
import {
  fmeaStatusStyle,
  rpnColor,
  FMEA_PURPLE,
} from "../data/mockData";
import { useFmeas, usePatchFmea } from "../api/sitroom";
import { RoleGuard } from "../components/RoleGuard";
import { useT } from "../lib/i18n";
import { NewProcessFmeaModal } from "../components/fmea/NewProcessFmeaModal";
import { useQueryClient } from "@tanstack/react-query";

const filterPillsIds = ["all", "pending", "approved", "rejected"];
const linkKindFilters = ["all", "standalone", "linked"];

const Avatar = ({ initials }) => (
  <div
    className="rounded-md flex items-center justify-center flex-shrink-0"
    style={{
      width: 32,
      height: 32,
      background: "rgba(142,68,173,0.18)",
      border: "1px solid rgba(142,68,173,0.5)",
      color: "#c89adb",
      fontFamily: "'DM Mono', monospace",
      fontSize: 10,
      fontWeight: 500,
    }}
  >
    {initials}
  </div>
);

const Row = ({ f, onOpen, onIncidentOpen, onApprove, onReject }) => {
  const sev = Number(f.severity) || 0;
  const occ = Number(f.occurrence) || 0;
  const det = Number(f.detection) || 0;
  const rpn = sev * occ * det;
  const rpnC = rpnColor(rpn);
  const st = fmeaStatusStyle(f.status);
  return (
    <div
      className="row-hover cursor-pointer"
      style={{
        background: "#0d0f18",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 8,
        padding: "14px 16px",
        marginBottom: 8,
      }}
      onClick={onOpen}
      data-testid={`fmea-row-${f.id}`}
    >
      <div className="flex items-start gap-3">
        <Avatar initials="FM" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c89adb" }}>
              {f.id}
            </span>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0" }}>·</span>
            {f.sourceIncident ? (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onIncidentOpen(f.sourceIncident);
                  }}
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 10,
                    color: "#9ec3ec",
                    textDecoration: "underline",
                    textUnderlineOffset: 2,
                  }}
                  data-testid={`fmea-source-link-${f.sourceIncident}`}
                >
                  {f.sourceIncident}
                </button>
                <span
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 9,
                    padding: "1px 6px",
                    borderRadius: 3,
                    background: "rgba(24,95,165,0.18)",
                    border: "1px solid rgba(24,95,165,0.5)",
                    color: "#9ec3ec",
                    letterSpacing: "0.8px",
                  }}
                  data-testid={`fmea-badge-linked-${f.id}`}
                >
                  LINKED
                </span>
              </>
            ) : (
              <span
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 9,
                  padding: "1px 6px",
                  borderRadius: 3,
                  background: "rgba(142,68,173,0.18)",
                  border: "1px solid rgba(142,68,173,0.5)",
                  color: "#c89adb",
                  letterSpacing: "0.8px",
                }}
                data-testid={`fmea-badge-standalone-${f.id}`}
              >
                STANDALONE
              </span>
            )}
            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 9,
                padding: "1px 6px",
                borderRadius: 3,
                background: "rgba(120,125,140,0.18)",
                border: "1px solid rgba(120,125,140,0.3)",
                color: "#9aa1b3",
                letterSpacing: "0.8px",
              }}
            >
              CLOSED
            </span>
            <span
              style={{
                background: st.bg,
                border: `1px solid ${st.border}`,
                color: st.color,
                fontFamily: "'DM Mono', monospace",
                fontSize: 9,
                padding: "2px 7px",
                borderRadius: 3,
                letterSpacing: "1px",
                fontWeight: 500,
              }}
            >
              {st.label}
            </span>
            {f.aiScored && (
              <span
                style={{
                  background: "rgba(24,95,165,0.18)",
                  border: "1px solid rgba(24,95,165,0.5)",
                  color: "#9ec3ec",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 9,
                  padding: "2px 7px",
                  borderRadius: 3,
                  letterSpacing: "1px",
                  fontWeight: 500,
                }}
                data-testid={`fmea-ai-scored-${f.id}`}
              >
                AI-SCORED
              </span>
            )}
          </div>
          <div
            style={{
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 700,
              fontSize: 14,
              color: "#f0f2f8",
              lineHeight: 1.3,
            }}
          >
            {f.failureMode}
          </div>
          <div
            className="mt-1"
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              color: "#c5cad8",
              lineHeight: 1.5,
            }}
          >
            <strong style={{ color: "#cfd1d8" }}>Effect:</strong> {f.effect}
          </div>
          <div
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              color: "#c5cad8",
              lineHeight: 1.5,
            }}
          >
            <strong style={{ color: "#cfd1d8" }}>Cause:</strong> {f.cause}
          </div>
        </div>

        {/* RPN block */}
        <div
          className="flex-shrink-0 text-center"
          style={{
            background: "#111420",
            border: `1px solid ${rpnC}55`,
            borderRadius: 6,
            padding: "8px 12px",
            minWidth: 88,
          }}
        >
          <div
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 8,
              letterSpacing: "1.5px",
              color: "#9198b0",
              textTransform: "uppercase",
            }}
          >
            RPN
          </div>
          <div
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 22,
              color: rpnC,
              fontWeight: 500,
              lineHeight: 1.1,
              marginTop: 2,
            }}
            data-testid={`fmea-rpn-${f.id}`}
          >
            {rpn}
          </div>
          <div
            className="mt-1 flex justify-center gap-1.5"
            style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0" }}
          >
            <span>S{sev}</span>
            <span>·</span>
            <span>O{occ}</span>
            <span>·</span>
            <span>D{det}</span>
          </div>
        </div>
      </div>

      {/* Recommended action */}
      <div
        className="mt-3 rounded-md p-2.5"
        style={{
          background: "rgba(142,68,173,0.07)",
          border: "1px solid rgba(142,68,173,0.18)",
        }}
      >
        <div
          className="flex items-center justify-between"
          style={{ marginBottom: 4 }}
        >
          <div
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 9,
              letterSpacing: "1.4px",
              color: "#c89adb",
              textTransform: "uppercase",
            }}
          >
            Recommended Action
          </div>
          {f.aiGenerated && (
            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 8,
                letterSpacing: "1px",
                color: "#c89adb",
                background: "rgba(142,68,173,0.12)",
                border: "1px solid rgba(142,68,173,0.35)",
                padding: "1px 6px",
                borderRadius: 3,
              }}
            >
              AI · CLAUDE
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: "#cfd1d8", lineHeight: 1.5 }}>{f.recommendedAction}</div>
      </div>

      {/* Extended FMEA columns row */}
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { l: "Business Function", v: f.businessFunction },
          { l: "Process Step", v: f.processStep },
          { l: "Responsibility", v: f.responsibility },
          { l: "Actions Taken", v: f.actionsTaken },
        ].map((c) => (
          <div
            key={c.l}
            className="rounded-md p-2"
            style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.06)" }}
          >
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 8, color: "#9198b0", letterSpacing: "1.2px", textTransform: "uppercase" }}>
              {c.l}
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#cfd1d8", marginTop: 2, lineHeight: 1.4 }}>
              {c.v || "—"}
            </div>
          </div>
        ))}
      </div>

      {/* Source links + New RPN row */}
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        {f.source8D && (
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9ec3ec" }}>
            ↗ source 8D: {f.source8D}
          </span>
        )}
        {(() => {
          const newSev = Number(f.newSeverity) || sev;
          const newOcc = Number(f.newOccurrence) || occ;
          const newDet = Number(f.newDetection) || det;
          const newRpn = newSev * newOcc * newDet;
          const newC = rpnColor(newRpn);
          return (
            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 10,
                color: newC,
                background: `${newC}14`,
                border: `1px solid ${newC}55`,
                padding: "2px 8px",
                borderRadius: 4,
              }}
            >
              NEW RPN: {newRpn} (S{newSev}·O{newOcc}·D{newDet})
            </span>
          );
        })()}
      </div>

      {/* Linked actions row */}
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <span
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 9,
            letterSpacing: "1.4px",
            color: "#9198b0",
            textTransform: "uppercase",
          }}
        >
          Linked Actions:
        </span>
        {f.jira && (
          <span
            style={{
              background: "rgba(0,82,204,0.16)",
              border: "1px solid rgba(0,82,204,0.5)",
              color: "#5fa3ff",
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
              padding: "2px 8px",
              borderRadius: 3,
              letterSpacing: "0.4px",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
            data-testid={`fmea-jira-badge-${f.id}`}
          >
            <span
              style={{
                width: 10,
                height: 10,
                background: "#0052CC",
                color: "#fff",
                borderRadius: 2,
                fontSize: 7,
                fontWeight: 700,
                textAlign: "center",
                lineHeight: "10px",
              }}
            >
              J
            </span>
            {f.jira.ticketId}
          </span>
        )}
        {f.planner && (
          <span
            style={{
              background: "rgba(123,94,167,0.16)",
              border: "1px solid rgba(123,94,167,0.5)",
              color: "#bda3d8",
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
              padding: "2px 8px",
              borderRadius: 3,
              letterSpacing: "0.4px",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              maxWidth: 220,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            data-testid={`fmea-planner-badge-${f.id}`}
            title={f.planner.taskTitle}
          >
            <span
              style={{
                width: 10,
                height: 10,
                background: "#7B5EA7",
                color: "#fff",
                borderRadius: 2,
                fontSize: 7,
                fontWeight: 700,
                textAlign: "center",
                lineHeight: "10px",
              }}
            >
              P
            </span>
            {f.planner.taskTitle.length > 22
              ? f.planner.taskTitle.slice(0, 22) + "…"
              : f.planner.taskTitle}
          </span>
        )}
        {!f.jira && !f.planner && (
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0" }}>
            —
          </span>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
        <RoleGuard
          minRole="escalation_manager"
          fallback={
            <span
              className="px-3 py-1.5 rounded-md"
              style={{
                background: "rgba(243,177,74,0.08)",
                border: "1px solid rgba(243,177,74,0.35)",
                color: "#f3b14a",
                fontFamily: "'DM Mono', monospace",
                fontSize: 10,
                letterSpacing: "0.6px",
              }}
              data-testid={`fmea-approve-pending-${f.id}`}
            >
              Pending manager approval
            </span>
          }
        >
          <button
            onClick={() => onApprove(f.id)}
            disabled={f.status === "approved"}
            className="px-3 py-1.5 rounded-md"
            style={{
              background: f.status === "approved" ? "rgba(39,174,96,0.06)" : "rgba(39,174,96,0.12)",
              border: "1px solid rgba(39,174,96,0.4)",
              color: "#4ed084",
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 600,
              fontSize: 11,
              opacity: f.status === "approved" ? 0.5 : 1,
              cursor: f.status === "approved" ? "default" : "pointer",
            }}
            data-testid={`fmea-approve-${f.id}`}
          >
            ✓ Approve
          </button>
          <button
            onClick={() => onReject(f.id)}
            disabled={f.status === "rejected"}
            className="px-3 py-1.5 rounded-md"
            style={{
              background: f.status === "rejected" ? "rgba(226,75,74,0.06)" : "rgba(226,75,74,0.10)",
              border: "1px solid rgba(226,75,74,0.4)",
              color: "#ff7e7c",
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 600,
              fontSize: 11,
              opacity: f.status === "rejected" ? 0.5 : 1,
              cursor: f.status === "rejected" ? "default" : "pointer",
            }}
            data-testid={`fmea-reject-${f.id}`}
          >
            ✗ Reject
          </button>
        </RoleGuard>
        <button
          onClick={onOpen}
          className="px-3 py-1.5 rounded-md ml-auto"
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#cfd1d8",
            fontFamily: "'Syne', system-ui, sans-serif",
            fontWeight: 600,
            fontSize: 11,
          }}
          data-testid={`fmea-edit-${f.id}`}
        >
          ✏ Edit
        </button>
      </div>
    </div>
  );
};

export default function FMEA() {
  const fmeaFilter = useStore((s) => s.fmeaFilter);
  const setFmeaFilter = useStore((s) => s.setFmeaFilter);
  const fmeaSearch = useStore((s) => s.fmeaSearch);
  const setFmeaSearch = useStore((s) => s.setFmeaSearch);
  const openFmeaPanel = useStore((s) => s.openFmeaPanel);
  const openPanel = useStore((s) => s.openPanel);
  const addToast = useStore((s) => s.addToast);
  const { t } = useT();
  const qc = useQueryClient();

  const [linkKindFilter, setLinkKindFilter] = useState("all");
  const [showNewModal, setShowNewModal] = useState(false);

  const { data: list = [] } = useFmeas();
  const patchMutation = usePatchFmea();

  const filtered = useMemo(() => {
    let l = list;
    if (fmeaFilter !== "all") l = l.filter((f) => f.status === fmeaFilter);
    if (linkKindFilter === "standalone") l = l.filter((f) => !f.sourceIncident && !f.incident_id);
    if (linkKindFilter === "linked") l = l.filter((f) => !!(f.sourceIncident || f.incident_id));
    if (fmeaSearch.trim()) {
      const q = fmeaSearch.toLowerCase();
      l = l.filter(
        (f) =>
          (f.id || "").toLowerCase().includes(q) ||
          (f.failureMode || "").toLowerCase().includes(q) ||
          (f.sourceIncident || "").toLowerCase().includes(q) ||
          (f.title || "").toLowerCase().includes(q) ||
          (f.process_name || "").toLowerCase().includes(q) ||
          (f.cause || "").toLowerCase().includes(q)
      );
    }
    return l;
  }, [list, fmeaFilter, fmeaSearch, linkKindFilter]);

  const counts = {
    all: list.length,
    pending: list.filter((f) => f.status === "pending").length,
    approved: list.filter((f) => f.status === "approved").length,
    rejected: list.filter((f) => f.status === "rejected").length,
  };
  const linkCounts = {
    all: list.length,
    standalone: list.filter((f) => !f.sourceIncident && !f.incident_id).length,
    linked: list.filter((f) => !!(f.sourceIncident || f.incident_id)).length,
  };

  const handleApprove = (id) => {
    patchMutation.mutate({ id, patch: { status: "approved", reviewedBy: "G. Castro" } });
    addToast({
      type: "info",
      title: "FMEA approved",
      body: `${id} added to FMEA registry`,
    });
  };
  const handleReject = (id) => {
    patchMutation.mutate({ id, patch: { status: "rejected", reviewedBy: "G. Castro" } });
    addToast({
      type: "info",
      title: "FMEA rejected",
      body: `${id} marked rejected`,
    });
  };

  return (
    <div data-testid="fmea-view">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1
            style={{
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 800,
              fontSize: "clamp(26px, 3.4vw, 34px)",
              color: "#f0f2f8",
              letterSpacing: "-0.5px",
              lineHeight: 1.05,
            }}
            data-testid="view-title"
          >
            {t("fmea.title")}
          </h1>
          <div
            className="mt-1.5 font-dm-mono"
            style={{ fontSize: 11, color: "#c5cad8", letterSpacing: "0.4px" }}
          >
            {t("fmea.subtitle")}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            className="px-3 py-2 rounded-md"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "#cfd1d8",
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 600,
              fontSize: 12,
            }}
            data-testid="fmea-export-btn"
          >
            Export XLSX
          </button>
          <button
            onClick={() => setShowNewModal(true)}
            className="px-3 py-2 rounded-md"
            style={{
              background: FMEA_PURPLE,
              color: "#fff",
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: "0.4px",
              border: "none",
              cursor: "pointer",
            }}
            data-testid="fmea-new-btn"
          >
            ＋ New Process FMEA
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex gap-2 flex-wrap">
          {filterPillsIds.map((id) => {
            const active = fmeaFilter === id;
            const labelMap = {
              all: t("fmea.filter_all"),
              pending: t("fmea.filter_pending"),
              approved: t("fmea.filter_approved"),
              rejected: t("fmea.filter_rejected"),
            };
            return (
              <button
                key={id}
                onClick={() => setFmeaFilter(id)}
                className="px-3 py-1.5 rounded-full"
                style={{
                  background: active ? FMEA_PURPLE : "transparent",
                  border: `1px solid ${active ? FMEA_PURPLE : "rgba(255,255,255,0.1)"}`,
                  color: active ? "#fff" : "#9aa1b3",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 10,
                  letterSpacing: "0.4px",
                }}
                data-testid={`fmea-filter-${id}`}
              >
                {labelMap[id]} ({counts[id]})
              </button>
            );
          })}
          {/* Iter48 — standalone vs linked filter */}
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#3a3f50", padding: "6px 4px" }}>·</span>
          {linkKindFilters.map((id) => {
            const active = linkKindFilter === id;
            const label = id === "all" ? "All FMEAs" : id === "standalone" ? "Standalone" : "Incident-linked";
            return (
              <button
                key={`lk-${id}`}
                onClick={() => setLinkKindFilter(id)}
                className="px-3 py-1.5 rounded-full"
                style={{
                  background: active ? "rgba(142,68,173,0.20)" : "transparent",
                  border: `1px solid ${active ? "rgba(142,68,173,0.55)" : "rgba(255,255,255,0.10)"}`,
                  color: active ? "#c89adb" : "#9aa1b3",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 10,
                  letterSpacing: "0.4px",
                }}
                data-testid={`fmea-linkkind-filter-${id}`}
              >
                {label} ({linkCounts[id]})
              </button>
            );
          })}
        </div>
        <div className="flex-1" />
        <input
          type="text"
          value={fmeaSearch}
          onChange={(e) => setFmeaSearch(e.target.value)}
          placeholder="Search FMEA…"
          className="rounded-md px-3 py-2"
          style={{
            width: 200,
            maxWidth: "100%",
            background: "#0d0f18",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#f0f2f8",
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
          }}
          data-testid="fmea-search-input"
        />
      </div>

      <div>
        {filtered.map((f) => (
          <Row
            key={f.id}
            f={f}
            onOpen={() => openFmeaPanel(f.id)}
            onIncidentOpen={(id) => openPanel(id)}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        ))}
        {filtered.length === 0 && (
          <div
            className="text-center py-10"
            style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9198b0" }}
          >
            No FMEA suggestions match your filter.
          </div>
        )}
      </div>

      <NewProcessFmeaModal
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ["fmeas"] });
          setShowNewModal(false);
        }}
      />
    </div>
  );
}
